import { type ServiceMethod } from '@/application/interfaces/service';

export type Params = {
  cartId: string;
};

export type DiscountTypes =
  | 'promo'
  | 'loyaltyProgram'
  | 'employee'
  | 'payment-method'
  | 'stampedPrice'
  | 'specialPrice'
  | 'twoPrices'
  | 'lys';

export type CartSummaryData = {
  serviceTotal: number;
  discount: number;
  freight: number;
  totalDiscountsByType?: Record<DiscountTypes, number>;
  subTotal: number;
  errors: { code: 'LYS_0'; message: string }[];
  items: Item[];
  total: {
    inCash: number;
    inInstallments: {
      maxQuantity: number;
      installmentValue: number;
      totalValue: number;
    };
  };
  pricesByPaymentMethods: {
    bankSlip: number;
    creditCard: number;
    pix: number;
  };
};

export type Item = {
  itemId: string;
  productId: number;
  quantity: number;
  prices: Prices;
  discounts: any[];
};

type Prices = {
  total: Total;
  unit: Unit;
};

type Unit = {
  original: number;
  inCash: number;
  inInstallment: number;
  pricesByPaymentMethods: PricesByPaymentMethods;
};

type Total = {
  inCash: number;
  inInstallment: number;
  pricesByPaymentMethods: PricesByPaymentMethods;
};

type PricesByPaymentMethods = {
  bankSlip: number;
  creditCard: number;
  pix: number;
};

export type Response = {
  data: CartSummaryData;
};

export type QueryParams = {
  payment_method?: PaymentMethod;
  simulate: 'true';
  installments?: number;
};

type PaymentMethod = 'pix' | 'credit_card' | 'bank_slip';

export interface Data extends Pick<QueryParams, 'installments'>, Params {
  paymentMethod?: PaymentMethod;
}

const getCartSummary: ServiceMethod<Response, Data> =
  (httpClient) => async (data, options) => {
    const { cartId, paymentMethod, installments } = data;

    const { body: response } = await httpClient.get<
      Response,
      Params,
      QueryParams
    >('/api/v3/carts/{cartId}/summary', {
      ...options,
      searchParams: {
        payment_method: paymentMethod,
        simulate: 'true',
        installments,
      },
      params: {
        cartId,
      },
    });

    return response;
  };

export default getCartSummary;
